/// <reference types="@types/google.maps" />
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { loadGoogleMaps } from '@/lib/googleMapsLoader';
import './GooglePlacesAutocomplete.css';

// Extend Window interface to include google
declare global {
  interface Window {
    google: typeof google;
  }
}

interface GooglePlacesAutocompleteProps {
  value: string;
  onChange: (address: string) => void;
  onPlaceSelect: (place: {
    address: string;
    lat: number;
    lng: number;
    place_id: string;
  }) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function GooglePlacesAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = 'Digite o endereço...',
  disabled = false
}: GooglePlacesAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<any>(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Usar refs para as callbacks para evitar re-renders
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  
  // Atualizar refs quando as props mudarem
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    let cancelled = false;
    
    setLoadError(null);
    console.log('🚀 Iniciando carregamento do Google Maps...');
    
    loadGoogleMaps()
      .then(() => {
        if (!cancelled) {
          console.log('✅ Google Maps carregado com sucesso');
          setIsGoogleLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('❌ Erro ao carregar Google Maps:', err);
          setLoadError(err?.message || 'Não foi possível carregar o Google Maps. Verifique a chave da API.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || !isGoogleLoaded || typeof window === 'undefined' || !window.google?.maps?.places) return;

    console.log('🔧 Inicializando PlaceAutocompleteElement...');

    // Criar input HTML normal
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    input.value = value;
    input.disabled = disabled;
    
    // Estilizar para parecer com Input do shadcn
    input.className = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

    // Inicializar Autocomplete com o input criado
    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: 'br' },
      fields: ['formatted_address', 'geometry', 'name', 'place_id'],
      types: ['geocode']
    });

    // Listener para quando usuário seleciona um lugar
    autocomplete.addListener('place_changed', () => {
      console.log('📍 Place selecionado');
      
      const place = autocomplete.getPlace();
      
      if (!place || !place.geometry) {
        console.warn('⚠️ Nenhum place válido retornado');
        return;
      }

      const selectedPlace = {
        address: place.formatted_address || place.name || '',
        lat: place.geometry.location!.lat(),
        lng: place.geometry.location!.lng(),
        place_id: place.place_id || ''
      };
      
      console.log('✅ Place selecionado:', selectedPlace);
      onChangeRef.current(selectedPlace.address);
      onPlaceSelectRef.current(selectedPlace);
    });

    // Listener para digitação manual
    input.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      onChangeRef.current(target.value);
    });

    // Limpar container e adicionar input
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(input);
    autocompleteRef.current = autocomplete;

    console.log('✅ PlaceAutocompleteElement inicializado');

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isGoogleLoaded, placeholder, disabled]);

  // Sincronizar valor quando a prop value mudar
  useEffect(() => {
    if (!containerRef.current || !isGoogleLoaded) return;
    
    const input = containerRef.current.querySelector('input');
    if (input && input.value !== value) {
      input.value = value;
    }
  }, [value, isGoogleLoaded]);

  if (loadError) {
    return (
      <div className="space-y-2">
        <div className="w-full h-9 px-3 py-2 border border-destructive rounded-md bg-background opacity-50">
          {placeholder}
        </div>
        <p className="text-xs text-destructive">{loadError}</p>
      </div>
    );
  }

  if (!isGoogleLoaded) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-full h-9 px-3 py-2 border border-input rounded-md bg-background opacity-50">
          Carregando Google Maps...
        </div>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <div ref={containerRef} className="w-full" />;
}
